import * as z from 'zod'

import {
  ZApiOptions,
  ZBundleInfo,
  ZCommonProperties,
  ZEntityDetails,
  ZFullLocation,
} from './commonSchema'
import { PageEventType, TrackEventType } from './eventTypes'

import { ErrorStates } from 'hooks/useSingleTrackAudioPlayer/types'

/** The trigger for the `track` */
enum EntityActionTrigger {
  /** Triggered from user action (directly) */
  USER = 'user',
  /** Triggered from system action (indirectly) */
  SYSTEM = 'system',
}

/** Type of entity action */
enum EntityActionType {
  OBJECT_SAVED = 'Object Saved',
  EXTERNAL_LINK_SELECTED = 'External Link Selected',
}

/** Commmon Props to all track events */
const ZTrackEventCommon = z
  .object({
    apiOptions: ZApiOptions,
  })
  .strict()

/** Common props to all entity-action track events */
const ZEntityActionCommonProps = z
  .object({
    /** The entity details of the entity that the action is being taken on */
    target: ZEntityDetails,

    /** What triggered the event, system, user, or something else */
    trigger: z.nativeEnum(EntityActionTrigger),

    /** Device location, if known */
    device_location: ZFullLocation.optional(),

    /** See the media played event for more information about these */
    contextual_entities: z.array(ZEntityDetails).default([]),

    /** The bundle details */
    bundle: ZBundleInfo,
  })
  .strict()

/** Common props for all visitor events */
const ZVisitorEventCommonProps = z
  .object({
    page_title: z.nativeEnum(PageEventType).nullable(),
    page_url: z.string().url().optional(),
    page_referrer: z.string().url().nullable().default(null),
    device_location: ZFullLocation.optional(),
    contextual_entities: z.array(ZEntityDetails).default([]),
    bundle: ZBundleInfo,
    locale: z.string().min(1),
  })
  .strict()

const _ZBaseCustomProps = z
  .object({
    /** Device Name */
    device_name: z.string().optional(),
    /** Device ID */
    device_id: z.string().optional(),
    /** Connection type */
    connection_type: z.string().optional(),
  })
  .strict()

/** Custom props included for all track events */
const ZCustomProps = _ZBaseCustomProps.default({})

/**
 * Track events (everything that isn't a page event)
 *
 * This is a discriminated union of possible track events, which include:
 *
 * - MEDIA_PLAYED
 * - ENTITY_ACTION
 * - A bunch of Visitor Actions Events see
 *  https://artprocessors.atlassian.net/wiki/spaces/APS/pages/2060943391/Event+Sync+PWA+-+Tracking+plan+-+Product+health
 */
export const ZTrackEvent = z.discriminatedUnion('event', [
  /**
   * Media Played
   * @see https://artprocessors.atlassian.net/wiki/spaces/DAI/pages/1998356528/Pladia+Visitor+Apps+-+Digital+Event+Spec+v0.1#Media-Played
   */
  ZTrackEventCommon.extend({
    event: z.literal(TrackEventType.MEDIA_PLAYED),

    properties: ZCommonProperties.extend({
      /** The entity details of the media being played */
      target: ZEntityDetails,

      /** The entity details of the media group if we have one */
      group: ZEntityDetails.optional(),

      /** The language of the media, default to the device language if we only have that */
      language: z.string().min(1),

      /** The duration of the media */
      duration: z.number().min(0),

      /** Track amount played, should never be higher then duration */
      duration_played: z.number().min(0),

      /** Device location, if known */
      device_location: ZFullLocation.optional(),

      /**
       * The contextual entities for this event. For media played events the
       * contextual entity is the entity details of the owner of the player.
       * Owner is defined as the parent entity of the player.
       *
       * For example: When the media is played as part of catalog-object details
       * page: the owner will be a reference to the catalog object.
       *
       * When the media is played as part of a “spaces” detail page: the owner
       * will be a reference to the space feature
       */
      contextual_entities: z.array(ZEntityDetails).default([]),

      /** The bundle details */
      bundle: ZBundleInfo,

      /** Custom object of anything for ED data. Must be sent, even empty */
      custom: z
        .object({
          connection_type: z.string().optional(),
          content_id: z.string().optional(),
          sequence: z.number().optional(),
          long_audio: z.boolean().optional(),
        })
        .default({
          connection_type: '',
          content_id: '',
          sequence: 0,
          long_audio: false,
        }),
    }).strict(),
  }).strict(),

  /**
   * Entity Action
   * @see https://artprocessors.atlassian.net/wiki/spaces/DAI/pages/1998356528/Pladia+Visitor+Apps+-+Digital+Event+Spec+v0.1#Entity-Action
   */
  ZTrackEventCommon.extend({
    event: z.literal(TrackEventType.ENTITY_ACTION),

    properties: z.discriminatedUnion('action', [
      ZEntityActionCommonProps.extend({
        action: z.literal(EntityActionType.OBJECT_SAVED),

        custom: z.object({}),
      }).strict(),

      ZEntityActionCommonProps.extend({
        action: z.literal(EntityActionType.EXTERNAL_LINK_SELECTED),

        custom: z.object({
          external_url: z.string().url(),
        }),
      }).strict(),
    ]),
  }).strict(),

  /**
   * Visitor events
   * @see https://artprocessors.atlassian.net/wiki/spaces/APS/pages/1998356528/Pladia+Visitor+Apps+-+Digital+Event+Spec+v0.1#Visitor-Events
   */
  ZTrackEventCommon.extend({
    event: z.enum([
      // Nothing
      TrackEventType.START_LISTENING_BUTTON_CLICKED,
      TrackEventType.PLAY_BUTTON_CLICKED,
      TrackEventType.MUTE_BUTTON_CLICKED,
      TrackEventType.UNMUTE_BUTTON_CLICKED,
      TrackEventType.AUDIO_STARTED_PLAYBACK,
      TrackEventType.TRANSCRIPT_OPENED,
      TrackEventType.TRANSCRIPT_CLOSED,
      TrackEventType.INFO_MODAL_OPENED,
      TrackEventType.INFO_MODAL_CLOSED,
    ]),

    properties: ZVisitorEventCommonProps.extend({
      custom: ZCustomProps,
    }).strict(),
  }).strict(),

  /**
   * Callback captures
   * @see https://artprocessors.atlassian.net/wiki/spaces/APS/pages/2060943391/Event+Sync+PWA+-+Tracking+plan+-+Product+health#2025-Analytics-Capture-Callbacks
   */
  ZTrackEventCommon.extend({
    event: z.enum([
      TrackEventType.PLAY,
      TrackEventType.PLAY_NEW_TRACK,
      TrackEventType.ACTIVE_FINISHED,
      TrackEventType.ACTIVE_FINISHED_AT_INIT,
    ]),

    properties: ZVisitorEventCommonProps.extend({
      custom: z
        .object({
          content_id: z.string().optional(),
        })
        .default({
          content_id: '',
        }),
    }).strict(),
  }).strict(),

  ZTrackEventCommon.extend({
    event: z.literal(TrackEventType.PLAY_ERROR),

    properties: ZVisitorEventCommonProps.extend({
      custom: z
        .object({
          content_id: z.string().optional(),
          circumstance: z.nativeEnum(ErrorStates).optional(),
        })
        .default({
          content_id: '',
          circumstance: undefined,
        }),
    }).strict(),
  }).strict(),

  ZTrackEventCommon.extend({
    event: z.literal(TrackEventType.MUTE_TOGGLE),

    properties: ZVisitorEventCommonProps.extend({
      custom: z
        .object({
          isNowMuted: z.boolean(),
        })
        .default({
          isNowMuted: false,
        }),
    }).strict(),
  }).strict(),
])

/**
 * The track event type, which is a combination of the common data and the track
 * specific data.
 */
export type TrackEvent = z.input<typeof ZTrackEvent>
